import React, { useEffect, useMemo } from "react";

import ceil from "lodash/ceil";
import { useFormContext } from "react-hook-form";
import { useParams } from "react-router-dom";

import useAccountHoldingsV2 from "@fartherfinance/frontend/api/Accounts/hooks/useAccountHoldingsV2";
import { FartherManagedAccount } from "@fartherfinance/frontend/api/Accounts/Types";
import { ExternalAccountExtended } from "@fartherfinance/frontend/api/ExternalAccount/hooks/useExternalAccountsExtended";
import { BalanceInfo } from "@fartherfinance/frontend/api/PerformanceGroups/hooks/useAllAccountBalances";
import { ClientId } from "@fartherfinance/frontend/api/Types";
import { SWEEP_ACCOUNT_SECURITY_SYMBOLS } from "@fartherfinance/frontend/constants/Securities/symbols";

import { CreateTransferWithdrawalFormData } from "../const";
import {
  createAccountLabel,
  createFundingAccountLabel,
  findAccountBalance,
} from "../utils";
import Button from "@src/multiCustodian/components/MUI/Button/Button";
import useApplicationMode from "@src/multiCustodian/hooks/useApplicationMode";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import FormDropdownField from "@src/sharedComponents/Forms/FormDropdownField";
import FormNumberField from "@src/sharedComponents/Forms/FormNumberField";
import FormTextField from "@src/sharedComponents/Forms/FormTextField";
import removeCommas from "@src/utils/CommaHelperFunctions/removeCommas";
import { formNumberFieldUpTo2DecimalsWithCommasCustomRegex } from "@src/utils/formValidation";
import toUSD from "@src/utils/toUSD";

interface CreateTransferWithdrawalFormProps {
  fundingAccount?: ExternalAccountExtended | null;
  managedAccounts: FartherManagedAccount[];
  allAccountBalances: BalanceInfo[];
}

const CreateTransferWithdrawalForm: React.FC<
  CreateTransferWithdrawalFormProps
> = ({ fundingAccount, managedAccounts, allAccountBalances }) => {
  const { clientId } = useParams<{ clientId: ClientId }>();
  const auth = useRequestAuth();
  const { isImpersonationMode } = useApplicationMode();

  const form = useFormContext<CreateTransferWithdrawalFormData>();
  const { watch, setError } = form;

  const withdrawalAccount = watch("withdrawalAccount");

  const withdrawalAccountId =
    withdrawalAccount?.account.virtualAccountId ?? null;

  const holdings = useAccountHoldingsV2(
    clientId,
    withdrawalAccountId
      ? {
          virtualAccountIds: [withdrawalAccountId],
          $expand: ["securityDetails"],
        }
      : null,
    auth
  );

  const cashInAccount = useMemo(() => {
    if (!holdings.data?.records) {
      return null;
    }

    // Find cash and sweep account securities
    const cashLikeHoldings = holdings.data.records.filter((holding) => {
      const ticker = holding.ticker?.toUpperCase();
      return (
        ticker === "CASH" ||
        ticker === "US DOLLARS" ||
        (ticker && SWEEP_ACCOUNT_SECURITY_SYMBOLS.has(ticker as any))
      );
    });

    // Sum up market values of all cash-like securities
    const totalCashValue = cashLikeHoldings.reduce(
      (total, holding) => total + (holding.market_value || 0),
      0
    );

    return totalCashValue;
  }, [holdings]);

  const fundingAccountLabel = useMemo(
    () => createFundingAccountLabel(fundingAccount),
    [fundingAccount]
  );

  const validACHAccountsList = useMemo(() => {
    const validACHAccountIds =
      fundingAccount?.fundingDetails?.validAchAccountIds ?? [];

    const validACHAccounts = managedAccounts.filter((account) =>
      validACHAccountIds.includes(account.virtualAccountId)
    );

    return validACHAccounts.map((account) => {
      const accountBalance = findAccountBalance(
        account.virtualAccountId,
        allAccountBalances
      );

      return {
        label: createAccountLabel(account, accountBalance, isImpersonationMode),
        account,
      };
    });
  }, [
    fundingAccount,
    managedAccounts,
    allAccountBalances,
    isImpersonationMode,
  ]);

  const handleOnWithdrawAllClick = () => {
    form.setValue("withdrawalAmount", ceil(cashInAccount ?? 0, 2).toString(), {
      shouldValidate: true,
    });
  };

  useEffect(() => {
    if (fundingAccount === null) {
      setError("depositAccountLabel", {
        type: "custom",
        message: "No funding account set up",
      });
    }
  }, [setError, fundingAccount]);

  return (
    <>
      <FormDropdownField
        name="withdrawalAccount"
        label="Withdrawal from"
        disableSearch
        required="Please select a valid withdrawal account"
        values={validACHAccountsList}
      />

      <FormTextField
        name="depositAccountLabel"
        label="Deposit to"
        labelTooltip="To change your bank account, please contact your wealth advisor."
        compactTooltip
        readonly
        defaultValue={fundingAccountLabel}
      />

      <FormNumberField
        name="withdrawalAmount"
        label="Amount"
        startAdornment={"$"}
        customRegex={formNumberFieldUpTo2DecimalsWithCommasCustomRegex}
        required="Please provide a transfer amount"
        rules={{
          validate: {
            isMinimum: (value) => {
              const commaLessNumber = Number(removeCommas(value));

              return commaLessNumber < 1
                ? "Amount needs to be greater than or equal to $1"
                : true;
            },
            isMaximum: (value) => {
              const commaLessNumber = Number(removeCommas(value));

              return commaLessNumber > 100_000
                ? "Amount needs to be less than or equal to $100,000"
                : true;
            },
            isOverCashAmount: (value) => {
              const commaLessNumber = Number(removeCommas(value));

              const cashInAccountFormatted = toUSD(cashInAccount ?? 0);

              return cashInAccount !== null && commaLessNumber > cashInAccount
                ? `Amount needs to be less than or equal to your account's cash amount: ${cashInAccountFormatted}`
                : true;
            },
          },
        }}
        autoAddCommas={true}
      />

      <Button
        style={{ alignSelf: "flex-end" }}
        variant="outlined"
        buttonType="secondary"
        text="Withdrawal All"
        disabled={!withdrawalAccountId || holdings.isLoading}
        onClick={handleOnWithdrawAllClick}
      />
    </>
  );
};

export default CreateTransferWithdrawalForm;
